import { useEffect, useState } from 'react';
import { configAPI } from './client';

/**
 * The server's non-secret render hints (GET /api/config/client), fetched once
 * per page load and shared.
 *
 * Cached as a module-level promise rather than per-component state because three
 * separate components want it (the idle prompt and both file pickers) and it
 * never changes within a session. Without the cache each one issues its own
 * request for the same static answer.
 *
 * Every value here also has a fallback, and every one of them is re-checked by
 * the server. These drive display and pre-validation only -- a client that
 * never loads this config must still be safe, just less helpful.
 */

export const CONFIG_FALLBACK = {
  idle_warning_minutes: 15,
  idle_grace_seconds: 30,
  max_upload_mb: 25,
  post_logout_redirect_url: '',
  manual_url: '',
  // Shown by AppFooter.js. "dev" matches backend/app/VERSION's own committed
  // default, so a lost connection and an unbuilt local checkout render the
  // same placeholder rather than a blank line.
  app_version: 'dev',
  // AI affordances default OFF when the config fetch fails. Failing closed
  // here is the right way round: a hidden panel is a missing feature, while a
  // visible one that 400s on every click looks broken.
  ai_evaluation_enabled: false,
  ai_policy_guide_enabled: false,
  // The same flag under the name the UI uses since issue #103. Both are served
  // for one release so a cached bundle keeps working; the panel reads this one.
  drafting_assistant_enabled: false,
  // Whether a likeness pair can be checked for contradictions (issue #125).
  // Served pre-ANDed with ai_evaluation_enabled, so the frontend never has to
  // recombine two flags and risk getting the combination wrong.
  conflict_scan_enabled: false,
  consolidation_enabled: false,
  // Whether the Start Page offers "Ask a question" (issue #173). False here
  // costs only the semantic pill -- the lexical search box is unaffected,
  // because it calls no model and has no flag.
  semantic_search_enabled: false,
  // Mirrors backend config.py::CONTENT_FEATURES -- used only if the client
  // config fetch fails, so a lost connection degrades the editor toward
  // "nothing fancy" rather than crashing it.
  content_features: {
    bold: true, italic: true, underline: true, superscript: true, text_size: true,
    headings: true, lists: true, links: true, images: false, emoji: false,
  },
};

let cached = null;

export function fetchClientConfig() {
  if (!cached) {
    cached = configAPI.client()
      .then((response) => ({ ...CONFIG_FALLBACK, ...(response.data || {}) }))
      .catch(() => {
        // Allow a later attempt rather than caching the failure forever: a
        // transient error on first paint should not disable these hints for the
        // rest of the session.
        cached = null;
        return CONFIG_FALLBACK;
      });
  }
  return cached;
}

export function useClientConfig() {
  const [config, setConfig] = useState(CONFIG_FALLBACK);

  useEffect(() => {
    let active = true;
    fetchClientConfig().then((value) => {
      if (active) setConfig(value);
    });
    return () => { active = false; };
  }, []);

  return config;
}
